import { useState, useEffect } from "react";
import { 
  Plus, Mail, Edit2, Trash2, Copy, Send, Eye, 
  Zap, Clock, ChevronRight, Check, X, Loader2,
  FileText, AlertTriangle, Settings
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "../../components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../components/ui/tooltip";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import EmailTemplateEditor from "../../components/EmailTemplateEditor";

const EmailTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("templates");
  
  // Editor state
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Preview state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Test email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testTemplateId, setTestTemplateId] = useState(null);
  const [sendingTest, setSendingTest] = useState(false);
  
  // Automation editor state
  const [automationOpen, setAutomationOpen] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [templatesRes, automationsRes, triggersRes, variablesRes] = await Promise.all([
        axios.get(`${API}/email-templates`, { withCredentials: true }),
        axios.get(`${API}/email-templates/automations/list`, { withCredentials: true }),
        axios.get(`${API}/email-templates/triggers`, { withCredentials: true }),
        axios.get(`${API}/email-templates/variables`, { withCredentials: true })
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setAutomations(automationsRes.data.automations || []);
      setTriggers(triggersRes.data.triggers || []);
      setVariables(variablesRes.data.variables || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 403) {
        toast.error("Email templates require a Pro or higher plan");
      } else {
        toast.error("Failed to load email templates");
      }
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (template = null) => {
    if (template) {
      setEditingTemplate({ ...template });
    } else {
      setEditingTemplate({
        name: "",
        subject: "",
        body: "",
        trigger_type: null,
        is_active: true,
        send_delay_hours: 0
      });
    }
    setEditorOpen(true);
  };

  const saveTemplate = async (templateData) => {
    // Handle data from EmailTemplateEditor component
    const dataToSave = templateData || editingTemplate;
    
    if (!dataToSave.name?.trim()) {
      toast.error("Please enter a template name");
      return;
    }
    if (!dataToSave.subject?.trim()) {
      toast.error("Please enter an email subject");
      return;
    }
    if (!dataToSave.body?.trim()) {
      toast.error("Please enter email content");
      return;
    }

    setSaving(true);
    try {
      if (dataToSave.template_id) {
        await axios.put(
          `${API}/email-templates/${dataToSave.template_id}`,
          dataToSave,
          { withCredentials: true }
        );
        toast.success("Template updated");
      } else {
        await axios.post(
          `${API}/email-templates`,
          dataToSave,
          { withCredentials: true }
        );
        toast.success("Template created");
      }
      setEditorOpen(false);
      setEditingTemplate(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const duplicateTemplate = async (templateId) => {
    try {
      await axios.post(
        `${API}/email-templates/${templateId}/duplicate`,
        {},
        { withCredentials: true }
      );
      toast.success("Template duplicated");
      fetchAll();
    } catch (error) {
      toast.error("Failed to duplicate template");
    }
  };

  const deleteTemplate = async () => {
    if (!deleteConfirm) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/email-templates/${deleteConfirm}`,
        { withCredentials: true }
      );
      toast.success("Template deleted");
      setDeleteConfirm(null);
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete template");
    } finally {
      setDeleting(false);
    }
  };

  const openPreview = async (templateId) => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const response = await axios.post(
        `${API}/email-templates/${templateId}/preview`,
        {},
        { withCredentials: true }
      );
      setPreviewContent(response.data);
    } catch (error) {
      toast.error("Failed to generate preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openTestEmail = (templateId) => {
    setTestTemplateId(templateId);
    setTestEmail("");
    setTestEmailOpen(true);
  };

  const sendTestEmail = async () => {
    if (!testEmail.trim()) {
      toast.error("Please enter an email address");
      return;
    }

    setSendingTest(true);
    try {
      await axios.post(
        `${API}/email-templates/${testTemplateId}/test`,
        { template_id: testTemplateId, to_email: testEmail },
        { withCredentials: true }
      );
      toast.success(`Test email sent to ${testEmail}`);
      setTestEmailOpen(false);
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setSendingTest(false);
    }
  };

  // Automation functions
  const saveAutomation = async () => {
    if (!editingAutomation?.trigger_type) {
      toast.error("Please select a trigger");
      return;
    }
    if (!editingAutomation?.template_id) {
      toast.error("Please select a template");
      return;
    }

    setSaving(true);
    try {
      if (editingAutomation.automation_id) {
        await axios.put(
          `${API}/email-templates/automations/${editingAutomation.automation_id}`,
          editingAutomation,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API}/email-templates/automations`,
          editingAutomation,
          { withCredentials: true }
        );
      }
      toast.success("Automation saved");
      setAutomationOpen(false);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save automation");
    } finally {
      setSaving(false);
    }
  };

  const toggleAutomation = async (automation) => {
    try {
      await axios.put(
        `${API}/email-templates/automations/${automation.automation_id}`,
        { is_enabled: !automation.is_enabled },
        { withCredentials: true }
      );
      fetchAll();
      toast.success(automation.is_enabled ? "Automation disabled" : "Automation enabled");
    } catch (error) {
      toast.error("Failed to update automation");
    }
  };

  const deleteAutomation = async (automationId) => {
    try {
      await axios.delete(
        `${API}/email-templates/automations/${automationId}`,
        { withCredentials: true }
      );
      toast.success("Automation deleted");
      fetchAll();
    } catch (error) {
      toast.error("Failed to delete automation");
    }
  };

  const getTriggerName = (triggerType) => {
    const trigger = triggers.find(t => t.type === triggerType);
    return trigger?.name || triggerType;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Email Templates</h1>
          <p className="text-muted-foreground mt-1">
            Create custom email templates and automate client communications
          </p>
        </div>
        <Button onClick={() => openEditor()} data-testid="create-template-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="templates" data-testid="templates-tab">
            <FileText className="w-4 h-4 mr-2" />
            Templates ({templates.length})
          </TabsTrigger>
          <TabsTrigger value="automations" data-testid="automations-tab">
            <Zap className="w-4 h-4 mr-2" />
            Automations ({automations.length})
          </TabsTrigger>
        </TabsList>

        {/* Templates Tab */}
        <TabsContent value="templates" className="mt-6">
          {templates.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-lg border border-border">
              <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Templates Yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first email template to personalize client communications
              </p>
              <Button onClick={() => openEditor()}>
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </div>
          ) : (
            <div className="grid gap-4">
              {templates.map((template) => (
                <div
                  key={template.template_id}
                  className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-colors"
                  data-testid={`template-card-${template.template_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{template.name}</h3>
                        {template.is_default && (
                          <Badge variant="secondary" className="text-xs">Default</Badge>
                        )}
                        {template.trigger_type && (
                          <Badge variant="outline" className="text-xs">
                            {getTriggerName(template.trigger_type)}
                          </Badge>
                        )}
                        {!template.is_active && (
                          <Badge variant="destructive" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                        Subject: {template.subject}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        Created {new Date(template.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openPreview(template.template_id)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Preview</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openTestEmail(template.template_id)}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Send Test</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditor(template)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Edit</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => duplicateTemplate(template.template_id)}
                            >
                              <Copy className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Duplicate</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      
                      {!template.is_default && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                onClick={() => setDeleteConfirm(template.template_id)}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations" className="mt-6">
          <div className="mb-4 flex justify-end">
            <Button 
              onClick={() => {
                setEditingAutomation({
                  trigger_type: "",
                  template_id: "",
                  is_enabled: true,
                  send_delay_hours: 0
                });
                setAutomationOpen(true);
              }}
              data-testid="create-automation-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Automation
            </Button>
          </div>

          {automations.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-lg border border-border">
              <Zap className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Automations Set Up</h3>
              <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                Automations send emails automatically when certain events happen, 
                like when a booking is confirmed or a gallery is delivered.
              </p>
              <Button onClick={() => {
                setEditingAutomation({
                  trigger_type: "",
                  template_id: "",
                  is_enabled: true,
                  send_delay_hours: 0
                });
                setAutomationOpen(true);
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Automation
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {automations.map((automation) => (
                <div
                  key={automation.automation_id}
                  className={`bg-card border rounded-lg p-4 transition-colors ${
                    automation.is_enabled 
                      ? 'border-primary/30 bg-primary/5' 
                      : 'border-border opacity-60'
                  }`}
                  data-testid={`automation-card-${automation.automation_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Switch
                        checked={automation.is_enabled}
                        onCheckedChange={() => toggleAutomation(automation)}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{automation.trigger_name}</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">{automation.template_name}</span>
                        </div>
                        {automation.send_delay_hours > 0 && (
                          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Sends {automation.send_delay_hours} hour{automation.send_delay_hours !== 1 ? 's' : ''} after trigger
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => {
                          setEditingAutomation(automation);
                          setAutomationOpen(true);
                        }}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => deleteAutomation(automation.automation_id)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Available Triggers Info */}
          <div className="mt-8 bg-muted/30 rounded-lg p-4">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-500" />
              Available Triggers
            </h4>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {triggers.map((trigger) => {
                const hasAutomation = automations.some(a => a.trigger_type === trigger.type);
                return (
                  <div 
                    key={trigger.type}
                    className={`text-sm p-2 rounded-md ${
                      hasAutomation ? 'bg-primary/10 text-primary' : 'bg-background'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {hasAutomation && <Check className="w-3 h-3" />}
                      {trigger.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Template Editor - Full Screen */}
      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-6xl w-[95vw] h-[90vh] p-0 overflow-hidden">
          <EmailTemplateEditor
            template={editingTemplate}
            onSave={saveTemplate}
            onSendTest={openTestEmail}
            onCancel={() => setEditorOpen(false)}
            triggers={triggers}
            variables={variables}
            saving={saving}
            sendingTest={sendingTest}
          />
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Email Preview</DialogTitle>
            <DialogDescription>
              This is how your email will look with sample data
            </DialogDescription>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : previewContent && (
            <div className="space-y-4">
              <div>
                <Label>Subject</Label>
                <p className="mt-1 p-2 bg-muted rounded-md">{previewContent.subject}</p>
              </div>
              <div>
                <Label>Email Body</Label>
                <div 
                  className="mt-1 border rounded-lg overflow-hidden"
                  style={{ maxHeight: '500px', overflow: 'auto' }}
                >
                  <iframe
                    srcDoc={previewContent.html_content}
                    className="w-full h-[500px] border-0"
                    title="Email Preview"
                  />
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testEmailOpen} onOpenChange={setTestEmailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription>
              Send a test email with sample data to preview the template
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="test-email">Email Address</Label>
              <Input
                id="test-email"
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="your@email.com"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTestEmailOpen(false)}>
              Cancel
            </Button>
            <Button onClick={sendTestEmail} disabled={sendingTest}>
              {sendingTest ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Automation Editor Dialog */}
      <Dialog open={automationOpen} onOpenChange={setAutomationOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAutomation?.automation_id ? "Edit Automation" : "Create Automation"}
            </DialogTitle>
            <DialogDescription>
              Set up automatic emails when certain events happen
            </DialogDescription>
          </DialogHeader>

          {editingAutomation && (
            <div className="space-y-4">
              <div>
                <Label>When this happens...</Label>
                <Select
                  value={editingAutomation.trigger_type}
                  onValueChange={(v) => setEditingAutomation({ 
                    ...editingAutomation, 
                    trigger_type: v 
                  })}
                  disabled={!!editingAutomation.automation_id}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select trigger..." />
                  </SelectTrigger>
                  <SelectContent>
                    {triggers.map((trigger) => (
                      <SelectItem key={trigger.type} value={trigger.type}>
                        <div>
                          <p>{trigger.name}</p>
                          <p className="text-xs text-muted-foreground">{trigger.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Send this template...</Label>
                <Select
                  value={editingAutomation.template_id}
                  onValueChange={(v) => setEditingAutomation({ 
                    ...editingAutomation, 
                    template_id: v 
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.filter(t => t.is_active).map((template) => (
                      <SelectItem key={template.template_id} value={template.template_id}>
                        {template.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Delay (optional)</Label>
                <Select
                  value={String(editingAutomation.send_delay_hours || 0)}
                  onValueChange={(v) => setEditingAutomation({ 
                    ...editingAutomation, 
                    send_delay_hours: parseInt(v) 
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Send immediately</SelectItem>
                    <SelectItem value="1">Wait 1 hour</SelectItem>
                    <SelectItem value="24">Wait 1 day</SelectItem>
                    <SelectItem value="48">Wait 2 days</SelectItem>
                    <SelectItem value="72">Wait 3 days</SelectItem>
                    <SelectItem value="168">Wait 1 week</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  checked={editingAutomation.is_enabled}
                  onCheckedChange={(checked) => setEditingAutomation({ 
                    ...editingAutomation, 
                    is_enabled: checked 
                  })}
                />
                <Label>Enable automation</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutomationOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveAutomation} disabled={saving}>
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Automation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Template?
            </DialogTitle>
            <DialogDescription>
              This will permanently delete this template and any automations using it.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={deleteTemplate} disabled={deleting}>
              {deleting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
